import * as React from "react";
import {observer} from "mobx-react";
import type {
	BeforeCapture,
	DraggableLocation,
	DraggableProvided,
	DraggableStateSnapshot,
	DragStart,
	DragUpdate,
	DropResult,
	ResponderProvided,
} from "@hello-pangea/dnd";
import {DragDropContext, Draggable, Droppable} from "@hello-pangea/dnd";
import {withAppStores} from "../../../../AppStores";
import type {XyiconFeature} from "../../../../generated/api/base";
import type {IFieldColumn} from "../../../../data/models/Report";
import {Report} from "../../../../data/models/Report";
import type {IClsObj} from "../../../utils/ReactUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import type {TransportLayer} from "../../../../data/TransportLayer";
import type {AppState} from "../../../../data/state/AppState";
import type {Field, IFieldAdapter} from "../../../../data/models/field/Field";
import {LINKED_FIELD_STRING, REPORT_COUNT_FIELD} from "../../../../data/state/AppFields";
import {fieldEquals} from "../../../../data/models/field/FieldUtils";
import {ArrayUtils} from "../../../../utils/data/array/ArrayUtils";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import LinkIcon from "../../icons/link.svg?react";
import RemoveIcon from "../../icons/xmark.svg?react";
import AddIcon from "../../icons/plus.svg?react";
import DownIcon from "../../icons/arrow-narrow-bottom-align.svg?react";
import UpIcon from "../../icons/arrow-narrow-top-align.svg?react";
import {SearchFieldV5} from "../../input/search/SearchFieldV5";
import {TabViewV5} from "../../widgets/tab/TabViewV5";
import {featureTitles} from "../../../../data/state/AppStateConstants";
import {ToggleContainerV5} from "../../widgets/ToggleContainerV5/ToggleContainerV5";
import DragIcon from "../../icons/drag.svg?react";
import {MathUtils} from "../../../../utils/math/MathUtils";
import {colorPalette} from "../../styles/colorPalette";
import {getFieldTypeIcons} from "../../settings/modules/abstract/SettingsUtils";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import {isHiddenViewColumnV5} from "../../../../data/models/ViewUtils";
import {TabChildV5} from "./TabChildV5";
import {ColumnContainerV5Styled, ColumnEditorStyled, MoveButtonsStyled} from "./ColumnEditorV5.style";

interface IColumnEditorProps {
	readonly report?: Report;
	readonly title?: string;
	readonly leftHeader?: string;
	readonly rightHeader?: string;
	readonly feature: XyiconFeature;
	readonly linkedXyiconFieldsOn?: boolean;
	readonly addColumns: (fields: IFieldColumn[], index?: number, linkedField?: boolean) => void;
	readonly removeColumns: (fields: IFieldColumn[], linkedField?: boolean) => void;
	readonly reorderColumn: (fromIndex: number | number[], toIndex: number, linkedField?: boolean) => void;
	readonly getColumnRefIds: () => string[];
	readonly getLinkedFieldsColumnRefIds?: () => string[];
	readonly getFieldClassNames?: (refId: string) => IClsObj;

	readonly filterColumn?: (columnRefId: string) => boolean;
	readonly defaultOpenFeature?: XyiconFeature;

	readonly transport?: TransportLayer;
	readonly appState?: AppState;
	readonly height?: string;
}

interface IColumnEditorState {
	searchHidden: string;
	searchVisible: string;
	selected: IFieldColumn[];
	isDragging: boolean;
	isDraggingFromRight: boolean;
	openedFeatures: XyiconFeature[];
	tempOpenedFeature: XyiconFeature[];
	mounted: boolean;
	prohibitedDropDisplayFieldsArea: boolean;
	prohibitedDropDisplayLinkedFieldsArea: boolean;
	selectedTabId: string;
	hoveredFieldRefIdAndSectionName: string; // ${refId}_${sectionName}, for example: "f32_availableFields"
}

type IGroupedColumns = {
	feature: XyiconFeature;
	fieldColumns: IFieldColumn[];
};

@withAppStores("appState", "transport")
@observer
export class ColumnEditorV5 extends React.Component<IColumnEditorProps, IColumnEditorState> {
	private _lastSelectedColumn: {field: IFieldAdapter; isCtrlPressed: boolean} = {field: null, isCtrlPressed: false};
	private _root = document.querySelector("#appRootStyled");
	private _disablePointerEventClassName = "disable-pointer-events";

	constructor(props: IColumnEditorProps) {
		super(props);
		this.state = {
			searchHidden: "",
			searchVisible: "",
			selected: [],
			isDraggingFromRight: false,
			openedFeatures: [this.props.feature],
			tempOpenedFeature: [],
			mounted: false,
			prohibitedDropDisplayFieldsArea: false,
			prohibitedDropDisplayLinkedFieldsArea: false,
			selectedTabId: "0",
			isDragging: false,
			hoveredFieldRefIdAndSectionName: "",
		};
	}

	public override componentDidUpdate(prevProps: IColumnEditorProps) {
		if (prevProps.linkedXyiconFieldsOn !== this.props.linkedXyiconFieldsOn) {
			this.setState({selected: []});

			if (prevProps.linkedXyiconFieldsOn) {
				this.onChangeSelectedTabId("0");
			}
		}
	}

	private getColumns() {
		const {getColumnRefIds, getLinkedFieldsColumnRefIds, filterColumn, feature, appState} = this.props;

		const columnRefIds = getColumnRefIds();
		const columnLinkedFieldsRefIds = getLinkedFieldsColumnRefIds?.() || [];

		const visibleColumns: IFieldColumn[] = columnRefIds
			.map((refId) => ({
				field: appState.actions.getFieldByRefId(refId),
				linkedField: false,
			}))
			.filter((columnField) => {
				if (!columnField.field) {
					return false;
				}
				// Note: not too nice to put it here, but we have to allow the report count field to be
				// enabled as an exception
				if (isHiddenViewColumnV5(columnField.field.refId)) {
					return false;
				}
				const isCountField = columnField.field?.refId === REPORT_COUNT_FIELD;
				const isValid = isCountField || appState.actions.isFieldValidForFeature(columnField.field, feature);

				return isValid && this.searchFilter(columnField.field, this.state.searchVisible);
			});

		const visibleLinkedFieldsColumns: IFieldColumn[] = columnLinkedFieldsRefIds
			.map((refId) => ({
				field: appState.actions.getFieldByRefId(refId),
				linkedField: true,
			}))
			.filter((columnField) => {
				// Note: not too nice to put it here, but we have to allow the report count field to be
				// enabled as an exception
				const isCountField = columnField.field?.refId === REPORT_COUNT_FIELD;
				const isValid = isCountField || appState.actions.isFieldValidForFeature(columnField.field, feature);

				return isValid && this.searchFilter(columnField.field, this.state.searchVisible);
			});

		const hiddenColumns: IGroupedColumns[] = [];
		const hiddenLinkedFieldsColumns: IGroupedColumns[] = [];
		const groups = appState.actions.getInheritedFeatures(feature);

		groups.forEach((groupFeature: XyiconFeature) => {
			hiddenColumns.push({
				feature: groupFeature,
				fieldColumns: [
					...appState.defaultFields[groupFeature].map((f) => ({field: f, linkedField: false})),
					...appState.fields[groupFeature].map((f) => ({field: f, linkedField: false})),
				].filter((fieldColumn) => {
					if (!appState.actions.isFieldValidForFeature(fieldColumn.field, feature)) {
						return false;
					}

					if (isHiddenViewColumnV5(fieldColumn.field.refId)) {
						return false;
					}

					return (
						!columnRefIds.find((columnRefIds) => fieldEquals(columnRefIds, fieldColumn.field.refId)) && // fields that are not added already
						this.searchFilter(fieldColumn.field, this.state.searchHidden) &&
						(!filterColumn || filterColumn(fieldColumn.field.refId))
					);
				}),
			});

			if (this.props.linkedXyiconFieldsOn) {
				hiddenLinkedFieldsColumns.push({
					feature: groupFeature,
					fieldColumns: [
						...appState.defaultFields[groupFeature].map((f) => ({field: f, linkedField: true})),
						...appState.fields[groupFeature].map((f) => ({field: f, linkedField: true})),
					].filter((fieldColumn) => {
						if (!appState.actions.isFieldValidForFeature(fieldColumn.field, feature)) {
							return false;
						}

						return (
							!columnLinkedFieldsRefIds.find((columnRefIds) => fieldEquals(columnRefIds, fieldColumn.field.refId)) && // fields that are not added already
							this.searchFilter(fieldColumn.field, this.state.searchHidden) &&
							(!filterColumn || filterColumn(fieldColumn.field.refId))
						);
					}),
				});
			}
		});

		return {hiddenColumns, hiddenLinkedFieldsColumns, visibleColumns, visibleLinkedFieldsColumns};
	}

	private searchFilter(field: IFieldAdapter, searchText: string) {
		if (searchText) {
			const fieldName = field?.name.toLowerCase() || "";

			return fieldName.includes(searchText.toLowerCase());
		}

		return true;
	}

	private onSearchHiddenChange = (value: string) => {
		const {hiddenColumns} = this.getColumns();
		const {searchHidden, openedFeatures, tempOpenedFeature} = this.state;

		let features: XyiconFeature[] = [];

		ArrayUtils.copy(features, openedFeatures);

		if (!searchHidden && value) {
			this.setState({tempOpenedFeature: features});
		} else if (searchHidden && !value) {
			this.setState({
				openedFeatures: tempOpenedFeature,
				tempOpenedFeature: [],
			});
		}

		if (value) {
			hiddenColumns.forEach((col) =>
				this.onToggle(!!col.fieldColumns.find((fieldColumn) => StringUtils.containsIgnoreCase(fieldColumn.field.name, value)), col.feature),
			);
		}

		this.updateSelection(value);
		this.setState({searchHidden: value});
	};

	private updateSelection(searchText: string) {
		const {selected} = this.state;

		if (selected.length > 0) {
			// Remove items from selection that don't match the filter
			const newSelected = selected.filter((f) => this.searchFilter(f.field, searchText));

			this.setState({selected: newSelected});
		}
	}

	private onShiftClick = (
		newIndex: number,
		lastIndex: number,
		selected: IFieldColumn[],
		allColumns: IFieldColumn[],
		clickedColumn: IFieldColumn,
		isAbleToSelectAll: boolean,
	) => {
		if (selected?.some((col) => col.field === this._lastSelectedColumn.field)) {
			let newSelection = this._lastSelectedColumn.isCtrlPressed ? [...selected] : [];
			const diff = newIndex - lastIndex;

			if (diff > 0) {
				for (let i = lastIndex; i <= newIndex; ++i) {
					newSelection.push(allColumns[i]);
				}
			} else if (diff < 0) {
				for (let i = lastIndex; i >= newIndex; --i) {
					newSelection.push(allColumns[i]);
				}
			}

			newSelection = ArrayUtils.removeDuplicates(newSelection);

			if (!isAbleToSelectAll && newSelection.length === allColumns.length) {
				return; // Don't allow selecting all visible columns
			}

			if (newSelection.length) {
				this.setState({selected: newSelection});
			}
		} else {
			this.setState({selected: [clickedColumn]});
		}
	};

	private onColumnClick = (selectedFieldColumn: IFieldColumn, event: React.MouseEvent) => {
		const {selected} = this.state;
		const selectedField = selected.find((f) => Report.isFieldColumnEquals(selectedFieldColumn, f));
		const {visibleColumns, visibleLinkedFieldsColumns, hiddenColumns, hiddenLinkedFieldsColumns} = this.getColumns();
		const allVisibleColumns = [...visibleColumns, ...visibleLinkedFieldsColumns];
		const allHiddenColumns = [
			...hiddenColumns.map((group) => group.fieldColumns.toSorted(this.sortFieldColumns)).flat(),
			...hiddenLinkedFieldsColumns.flatMap((group) => group.fieldColumns).toSorted(this.sortFieldColumns),
		];

		if (event.ctrlKey || event.metaKey) {
			this._lastSelectedColumn = {field: selectedFieldColumn.field, isCtrlPressed: true};

			if (selectedField) {
				// remove from selection
				this.setState({selected: ArrayUtils.remove(selected, selectedField)});
			} else if (selected.length > 0) {
				const isNewlySelectedFieldColumnInVisibleSection = allVisibleColumns.some((col) => Report.isFieldColumnEquals(col, selectedFieldColumn));
				const isNewlySelectedFieldColumnInHiddenSection = allHiddenColumns.some((col) => Report.isFieldColumnEquals(col, selectedFieldColumn));
				const areThereAnySelectedFieldColumnsInVisibleSection = selected.some((col) =>
					allVisibleColumns.some((visible) => Report.isFieldColumnEquals(visible, col)),
				);
				const areThereAnySelectedFieldColumnsInHiddenSection = selected.some((col) =>
					allHiddenColumns.some((hidden) => Report.isFieldColumnEquals(hidden, col)),
				);

				if (
					(areThereAnySelectedFieldColumnsInVisibleSection && isNewlySelectedFieldColumnInHiddenSection) ||
					(areThereAnySelectedFieldColumnsInHiddenSection && isNewlySelectedFieldColumnInVisibleSection)
				) {
					this.setState({selected: [selectedFieldColumn]});
				} else if (!!selected[0].linkedField === !!selectedFieldColumn?.linkedField) {
					// add to selection (only if the selected fields are equivalent with linkedField)
					const newSelection = [...selected, selectedFieldColumn];
					const newVisibleSelection = newSelection.filter((f) => allVisibleColumns.some((visible) => Report.isFieldColumnEquals(visible, f)));
					const allVisibleColumnsAreSelected = !selected[0].linkedField
						? visibleColumns.length === newVisibleSelection.length
						: visibleLinkedFieldsColumns.length === newVisibleSelection.length;

					if (allVisibleColumnsAreSelected) {
						// Don't allow selecting all visible columns
						return;
					}

					this.setState({selected: newSelection});
				}
			} else {
				this.setState({selected: [selectedFieldColumn]});
			}
		} else if (event.shiftKey) {
			const lastIndexVisible = allVisibleColumns.findIndex((col) => col.field === this._lastSelectedColumn.field);
			const newIndexVisible = allVisibleColumns.findIndex((col) => col.field === selectedFieldColumn.field);

			const lastIndexHidden = allHiddenColumns.findIndex((col) => col.field === this._lastSelectedColumn.field);
			const newIndexHidden = allHiddenColumns.findIndex((col) => col.field === selectedFieldColumn.field);

			if (lastIndexVisible > -1 && newIndexVisible > -1) {
				this.onShiftClick(newIndexVisible, lastIndexVisible, selected, allVisibleColumns, selectedFieldColumn, false);
			} else if (lastIndexHidden > -1 && newIndexHidden > -1) {
				this.onShiftClick(newIndexHidden, lastIndexHidden, selected, allHiddenColumns, selectedFieldColumn, true);
			}
		} else {
			this._lastSelectedColumn = {field: selectedFieldColumn.field, isCtrlPressed: false};

			if (allVisibleColumns.length === 1 && allVisibleColumns[0].field.refId === selectedFieldColumn.field.refId) {
				// Don't allow selecting all visible columns
				return;
			}

			this.setState({selected: [selectedFieldColumn]});
		}
	};

	private flattenColumns(groups: IGroupedColumns[]) {
		const list: IFieldColumn[] = [];

		groups.forEach((group) => {
			list.push(...group.fieldColumns);
		});

		return list;
	}

	private onMoveRightClick = (event: React.MouseEvent<Element, MouseEvent>, clickedFieldColumn: IFieldColumn) => {
		event.stopPropagation();
		const {addColumns} = this.props;
		const {hiddenColumns, hiddenLinkedFieldsColumns} = this.getColumns();
		const allHiddenColumns = [...hiddenColumns, ...hiddenLinkedFieldsColumns];
		const selected = this.getSelected(this.flattenColumns(allHiddenColumns));

		if (!selected.some((f) => Report.isFieldColumnEquals(f, clickedFieldColumn))) {
			selected.length = 0;
			selected.push(clickedFieldColumn);
			this.setState({selected});
		}

		if (selected.length > 0 && this.isSelectedFieldColumnsAreHomogene(selected)) {
			addColumns(selected, null, selected[0].linkedField);
			this.setState({selected: []});
		}
	};

	private onMoveLeftClick = (event: React.MouseEvent<Element, MouseEvent>, clickedFieldColumn: IFieldColumn) => {
		event.stopPropagation();
		const {removeColumns} = this.props;
		const {visibleColumns, visibleLinkedFieldsColumns} = this.getColumns();
		const allVisibleColumns = [...visibleColumns, ...visibleLinkedFieldsColumns];
		const selected = this.getSelected(allVisibleColumns);

		if (!selected.some((f) => Report.isFieldColumnEquals(f, clickedFieldColumn))) {
			selected.length = 0;
			selected.push(clickedFieldColumn);
			this.setState({selected});
		}

		if (selected.length > 0 && this.isSelectedFieldColumnsAreHomogene(selected)) {
			removeColumns(selected, selected[0].linkedField);
			this.setState({selected: []});
		}
	};

	private onMoveToTopClicked = (event: React.MouseEvent<Element, MouseEvent>, fieldColumn: IFieldColumn) => {
		event.stopPropagation();
		this.onMoveFieldColumns(fieldColumn, -Infinity);
	};

	private onMoveToBottomClicked = (event: React.MouseEvent<Element, MouseEvent>, fieldColumn: IFieldColumn) => {
		event.stopPropagation();
		this.onMoveFieldColumns(fieldColumn, Infinity);
	};

	private isSelected(fieldColumn: IFieldColumn) {
		return !!this.state.selected.find((fc) => Report.isFieldColumnEquals(fc, fieldColumn));
	}

	private getSelected(fieldColumns: IFieldColumn[]) {
		return fieldColumns.filter((fc) => this.isSelected(fc));
	}

	private onMoveFieldColumns = (clickedFieldColumn: IFieldColumn, offset: number) => {
		const {reorderColumn} = this.props;
		const {visibleColumns, visibleLinkedFieldsColumns} = this.getColumns();
		const allVisibleColumns = [...visibleColumns, ...visibleLinkedFieldsColumns];
		const selected = this.getSelected(allVisibleColumns);
		if (!selected.some((f) => Report.isFieldColumnEquals(f, clickedFieldColumn))) {
			selected.length = 0;
			selected.push(clickedFieldColumn);
			this.setState({selected});
		}

		if (selected) {
			if (!selected.every((f) => f.linkedField)) {
				const index = selected
					.map((selectedFieldColumn) => ({
						fc: selectedFieldColumn,
						i: visibleColumns.findIndex((fieldColumn) => Report.isFieldColumnEquals(fieldColumn, selectedFieldColumn)),
					}))
					.toSorted((a, b) => a.i - b.i)[0].i;
				const targetIndex = MathUtils.clamp(index + offset, 0, visibleColumns.length - selected.length);
				const fromIndexArray = selected.map((col) => visibleColumns.findIndex((fieldColumn) => Report.isFieldColumnEquals(fieldColumn, col)));

				reorderColumn(fromIndexArray.length === 1 ? fromIndexArray[0] : fromIndexArray, targetIndex);
			} else {
				const index = selected
					.map((s) => ({fc: s, i: visibleLinkedFieldsColumns.findIndex((fieldColumn) => Report.isFieldColumnEquals(fieldColumn, s))}))
					.toSorted((a, b) => a.i - b.i)[0].i;
				const targetIndex = MathUtils.clamp(index + offset, 0, visibleLinkedFieldsColumns.length - selected.length);

				reorderColumn(
					selected.map((col) => visibleLinkedFieldsColumns.findIndex((fieldColumn) => Report.isFieldColumnEquals(fieldColumn, col))),
					targetIndex,
					true,
				);
			}
		}
	};

	private renderSelectionCount(snapshot: DraggableStateSnapshot) {
		const shouldShowSelection = snapshot.isDragging && this.state.selected.length > 1;

		return shouldShowSelection && <div className="selectionCount">{this.state.selected.length}</div>;
	}

	private renderField(
		col: IFieldColumn,
		sectionName: "availableFields" | "linkedFields",
		provided: DraggableProvided,
		snapshot: DraggableStateSnapshot,
		leftSide?: boolean,
	) {
		const {getFieldClassNames} = this.props;
		const isSelected = this.isSelected(col);

		const [hoveredFieldRefId, hoveredSectionName] = this.state.hoveredFieldRefIdAndSectionName.split("_");
		const isRowHovered =
			(hoveredFieldRefId === col.field.refId && hoveredSectionName === sectionName) || (isSelected && this.state.selected.length === 1);

		return (
			<div
				ref={provided.innerRef}
				{...provided.draggableProps}
				{...provided.dragHandleProps}
				className={ReactUtils.cls("column", {
					semiHidden: this.state.isDragging && !snapshot.isDragging && isSelected,
					linkedField: col.linkedField,
					selected: isSelected,
					...getFieldClassNames?.(col.field.refId),
				})}
				onClick={(event) => this.onColumnClick(col, event)}
				onMouseOver={() => this.setState({hoveredFieldRefIdAndSectionName: `${col.field.refId}_${sectionName}`})}
				onMouseLeave={() => this.setState({hoveredFieldRefIdAndSectionName: ""})}
			>
				<DragIcon className="drag" />
				{getFieldTypeIcons(col.field as Field)}
				{col.linkedField && <LinkIcon color={colorPalette.gray.c700Dark} />}
				<div className="name">{col.field.name}</div>
				{this.renderSelectionCount(snapshot)}
				{isRowHovered && (
					<MoveButtonsStyled>
						{leftSide ? (
							<IconButtonV5
								IconComponent={AddIcon}
								title="Add"
								onClick={(e) => this.onMoveRightClick(e, col)}
							/>
						) : (
							<>
								<IconButtonV5
									IconComponent={UpIcon}
									title="Move to top"
									onClick={(e) => this.onMoveToTopClicked(e, col)}
								/>
								<IconButtonV5
									IconComponent={DownIcon}
									title="Move to bottom"
									onClick={(e) => this.onMoveToBottomClicked(e, col)}
								/>
								<IconButtonV5
									IconComponent={RemoveIcon}
									title="Remove"
									onClick={(e) => this.onMoveLeftClick(e, col)}
								/>
							</>
						)}
					</MoveButtonsStyled>
				)}
			</div>
		);
	}

	private onToggle = (value: boolean, feature: XyiconFeature) => {
		const openedFeatures = this.state.openedFeatures;

		if (value) {
			ArrayUtils.addMutable(openedFeatures, feature);
		} else {
			ArrayUtils.removeMutable(openedFeatures, feature);
		}

		this.setState({openedFeatures: openedFeatures});
	};

	public override componentDidMount() {
		this.setState({mounted: true});
	}

	private onBeforeCapture = (initial: BeforeCapture) => {
		//Please do not delete it, this is fordebugging purposes.
		//console.log(`onBeforeCapture, draggableId: ${initial.draggableId}`)

		const {draggableId} = initial;
		const {visibleColumns, visibleLinkedFieldsColumns} = this.getColumns();
		const isOnRightSide = [...visibleColumns, ...visibleLinkedFieldsColumns].find((fieldColumn) => {
			const refId = fieldColumn.field.refId;

			return draggableId.replace(LINKED_FIELD_STRING, "") === refId && fieldColumn.linkedField === draggableId.includes(LINKED_FIELD_STRING);
		});

		if (isOnRightSide) {
			this.setState({isDraggingFromRight: true});
		}
	};

	private onDragStart = (initial: DragStart) => {
		const {source, draggableId} = initial;
		if (!this.state.isDragging) {
			this.setState({
				isDragging: true,
			});
		}
		//Please do not delete it, this is fordebugging purposes.
		//console.log(`onDragStart, source: ${source.droppableId}, draggableId: ${draggableId}`)

		if (this._root) {
			this._root.classList.add(this._disablePointerEventClassName);
		}

		const draggingItemRefId = draggableId.replace(LINKED_FIELD_STRING, "");
		const isLinkedField = draggableId.includes(LINKED_FIELD_STRING);

		if (!this.state.selected.some((f) => f.field.refId === draggingItemRefId && f.linkedField === isLinkedField)) {
			// dragged item is not in selection -> clear selection to contain only the dragged item
			this.setState({
				selected: [
					{
						field: this.props.appState.actions.getFieldByRefId(draggingItemRefId),
						linkedField: isLinkedField,
					},
				],
			});
		}

		this.forceUpdate();
	};

	private onDragUpdate = (initial: DragUpdate, provided: ResponderProvided) => {
		const {source, destination, draggableId} = initial;
		//Please do not delete it, this is fordebugging purposes.
		//console.log(`onDragUpdate, source: ${source.droppableId} | ${source.index}, destination: ${destination?.droppableId} | ${destination?.index}, draggableId: ${draggableId}`)

		this.setProhibititedDropArea(source, destination);
	};

	private handleReorder = (result: DropResult, selected: IFieldColumn[], columns: IFieldColumn[], isLinked = false) => {
		const {source, destination} = result;

		if (selected.length > 1) {
			const fromIndices = selected
				.map((col) => columns.findIndex((fieldColumn) => Report.isFieldColumnEquals(fieldColumn, col)))
				.toSorted((a, b) => a - b);
			let destinationIndex = destination.index;
			if (destinationIndex > fromIndices[0]) {
				destinationIndex -= fromIndices.length - 1;
			}
			destinationIndex = MathUtils.clamp(destinationIndex, 0, columns.length - 1);

			this.props.reorderColumn(fromIndices, destinationIndex, isLinked);

			this.setState({selected: []});
		} else {
			this.props.reorderColumn(source.index, destination.index, isLinked);
		}
	};

	private onDragEnd = (result: DropResult) => {
		if (this.state.isDragging) {
			this.setState({
				isDragging: false,
			});
		}
		const {visibleColumns, visibleLinkedFieldsColumns} = this.getColumns();
		const {source, destination} = result;
		const allVisibleColumns = [...visibleColumns, ...visibleLinkedFieldsColumns];
		const selected = this.getSelected(allVisibleColumns);

		// Depending on the ID needed to get the correct item to update we may need to change the <Draggable />s id
		// and retrieve it with string manipulation e.g. "draggableId.split("-")[i]"

		//Please do not delete it, this is fordebugging purposes.
		//console.log(`onDragEnd, source: ${source.droppableId}, destination: ${destination?.droppableId}, draggableId: ${draggableId}`)

		if (this._root) {
			this._root.classList.remove(this._disablePointerEventClassName);
		}

		if (destination !== null) {
			// Drag from right to left, reset item to original category
			if (source.droppableId === "visible-columns" && destination.droppableId === "drop-zone") {
				const selectedAndVisible = this.state.selected.filter((f) => visibleColumns.some((c) => c.field.refId === f.field.refId));

				this.props.removeColumns(selectedAndVisible);
				this.setState({selected: []});
			}

			if (source.droppableId === `visible-${LINKED_FIELD_STRING}columns` && destination.droppableId === "drop-zone") {
				const selectedAndVisible = this.state.selected.filter((f) => visibleLinkedFieldsColumns.some((c) => c.field.refId === f.field.refId));

				this.props.removeColumns(selectedAndVisible, true);
				this.setState({selected: []});
			}

			if (source.droppableId.includes("hidden") && !source.droppableId.includes("linkedFields") && destination.droppableId === "visible-columns") {
				const selectedAndHidden = this.state.selected.filter((f) => !visibleColumns.some((c) => c.field.refId === f.field.refId));

				this.props.addColumns(selectedAndHidden, destination.index);
				this.setState({selected: []});
			}

			if (source.droppableId.includes("hidden-linkedFields") && destination.droppableId === `visible-${LINKED_FIELD_STRING}columns`) {
				const selectedAndHidden = this.state.selected.filter((f) => !visibleLinkedFieldsColumns.some((c) => c.field.refId === f.field.refId));

				this.props.addColumns(selectedAndHidden, destination.index, true);
				this.setState({selected: []});
			}

			// Reorder visible items (right column)
			if (source.droppableId === "visible-columns" && destination.droppableId === "visible-columns") {
				this.handleReorder(result, selected, visibleColumns, false);
			}

			if (source.droppableId === `visible-${LINKED_FIELD_STRING}columns` && destination.droppableId === `visible-${LINKED_FIELD_STRING}columns`) {
				this.handleReorder(result, selected, visibleLinkedFieldsColumns, true);
			}
		}

		this.resetProhibitedAreas();

		// Reset hover
		this.setState({
			isDraggingFromRight: false,
		});
	};

	private setProhibititedDropArea(source: DraggableLocation, destination: DraggableLocation) {
		let prohibitedDropDisplayFieldsArea = false;
		let prohibitedDropDisplayLinkedFieldsArea = false;

		if (
			source.droppableId.includes("hidden") &&
			!source.droppableId.includes("linkedFields") &&
			destination?.droppableId === `visible-${LINKED_FIELD_STRING}columns`
		) {
			prohibitedDropDisplayLinkedFieldsArea = true;
		}

		if (source.droppableId.includes("linkedFields") && destination?.droppableId === "visible-columns") {
			prohibitedDropDisplayFieldsArea = true;
		}

		this.setState({prohibitedDropDisplayFieldsArea, prohibitedDropDisplayLinkedFieldsArea});
	}

	private resetProhibitedAreas() {
		this.setState({
			prohibitedDropDisplayFieldsArea: false,
			prohibitedDropDisplayLinkedFieldsArea: false,
		});
	}

	private onChangeSelectedTabId = (id: string) => {
		this.setState({
			selectedTabId: id,
			selected: [],
		});
	};

	private sortFieldColumns = (a: IFieldColumn, b: IFieldColumn) => {
		if (a.field.name === "ID") {
			return -1;
		}
		if (b.field.name === "ID") {
			return 1;
		}

		return StringUtils.sortIgnoreCase(a.field.name, b.field.name);
	};

	private isSelectedFieldColumnsAreHomogene(selected: IFieldColumn[]) {
		return [...new Set(selected.map((f) => f.linkedField))].length === 1;
	}

	public override render() {
		const {title, leftHeader, rightHeader, defaultOpenFeature, linkedXyiconFieldsOn, height} = this.props;

		const {searchHidden, isDraggingFromRight, openedFeatures, prohibitedDropDisplayLinkedFieldsArea, prohibitedDropDisplayFieldsArea, selectedTabId} =
			this.state;

		const {hiddenColumns, hiddenLinkedFieldsColumns, visibleColumns, visibleLinkedFieldsColumns} = this.getColumns();
		const allVisibleColumns = [...visibleColumns, ...visibleLinkedFieldsColumns];
		const allVisibleColumnsAreSelected = allVisibleColumns.length === this.getSelected(allVisibleColumns).length;
		const onlyOneVisibleColumns = allVisibleColumns.length === 1;
		const columnContainerHeight = height ?? "30vh";

		return (
			<ColumnEditorStyled
				className="ColumnEditor"
				data-cy="column-editor"
			>
				{title && <h4>{title}</h4>}
				<DragDropContext
					onBeforeCapture={this.onBeforeCapture}
					onDragStart={this.onDragStart}
					onDragUpdate={this.onDragUpdate}
					onDragEnd={this.onDragEnd}
				>
					<div
						className="manage-cols-box"
						data-cy="manage-columns"
					>
						<div
							className="col left"
							data-cy="col left"
						>
							<TabViewV5
								selectedTabId={selectedTabId}
								onChangeSelectedTabId={this.onChangeSelectedTabId}
							>
								<TabChildV5
									label={leftHeader}
									title="Available Fields"
								>
									<SearchFieldV5
										placeholder="Find..."
										value={searchHidden}
										onInput={this.onSearchHiddenChange}
									/>
									<ColumnContainerV5Styled
										className="columnContainerV5"
										height={columnContainerHeight}
									>
										{!isDraggingFromRight ? (
											hiddenColumns.map((group) => (
												<Droppable
													key={`${featureTitles[group.feature]}`}
													droppableId={`hidden-${featureTitles[group.feature]}`}
													type="COLUMN"
												>
													{(provided) => (
														<div
															{...provided.droppableProps}
															ref={provided.innerRef}
														>
															<ToggleContainerV5
																key={group.feature}
																title={featureTitles[group.feature]}
																open={(!this.state.mounted && defaultOpenFeature === group.feature) || openedFeatures.includes(group.feature)}
																onToggleOpen={(value) => this.onToggle(value, group.feature)}
															>
																{
																	// First field always must be the ID, then alphabetical-order
																	group.fieldColumns.toSorted(this.sortFieldColumns).map((col, i) => (
																		<Draggable
																			key={col.field.refId}
																			draggableId={col.field.refId}
																			index={i}
																		>
																			{(provided, snapshot) => this.renderField(col, "availableFields", provided, snapshot, true)}
																		</Draggable>
																	))
																}
																{provided.placeholder}
															</ToggleContainerV5>
														</div>
													)}
												</Droppable>
											))
										) : (
											<div className="dropArea">
												<Droppable
													key="drop-zone"
													droppableId="drop-zone"
													type="COLUMN"
												>
													{(provided) => (
														<div
															className="droparea-context"
															{...provided.droppableProps}
															ref={provided.innerRef}
														>
															<span>Drop columns here</span>
															{provided.placeholder}
														</div>
													)}
												</Droppable>
											</div>
										)}
									</ColumnContainerV5Styled>
								</TabChildV5>
								{linkedXyiconFieldsOn && (
									<TabChildV5
										label="Linked Xyicon Fields"
										title="Linked Xyicon Fields"
									>
										<SearchFieldV5
											placeholder="Find..."
											value={searchHidden}
											onInput={this.onSearchHiddenChange}
										/>

										<ColumnContainerV5Styled
											className="columnContainerV5"
											height={columnContainerHeight}
										>
											{!isDraggingFromRight ? (
												hiddenLinkedFieldsColumns.map((group) => (
													<Droppable
														key={`linkedFields-${featureTitles[group.feature]}`}
														droppableId={`hidden-linkedFields-${featureTitles[group.feature]}`}
														type="COLUMN"
													>
														{(provided) => (
															<div
																className="droparea-context"
																{...provided.droppableProps}
																ref={provided.innerRef}
															>
																<ToggleContainerV5
																	key={group.feature}
																	title={featureTitles[group.feature]}
																	open={(!this.state.mounted && defaultOpenFeature === group.feature) || openedFeatures.includes(group.feature)}
																	onToggleOpen={(value) => this.onToggle(value, group.feature)}
																>
																	{group.fieldColumns.toSorted(this.sortFieldColumns).map((col, i) => (
																		<Draggable
																			key={`${LINKED_FIELD_STRING}${col.field.refId}`}
																			draggableId={`${LINKED_FIELD_STRING}${col.field.refId}`}
																			index={i}
																		>
																			{(provided, snapshot) => this.renderField(col, "linkedFields", provided, snapshot, true)}
																		</Draggable>
																	))}
																	{provided.placeholder}
																</ToggleContainerV5>
															</div>
														)}
													</Droppable>
												))
											) : (
												<div className="dropArea">
													<Droppable
														key="drop-zone"
														droppableId="drop-zone"
														type="COLUMN"
													>
														{(provided) => (
															<div
																{...provided.droppableProps}
																ref={provided.innerRef}
															>
																{!isDraggingFromRight && "Drop columns here"}
																{provided.placeholder}
															</div>
														)}
													</Droppable>
												</div>
											)}
										</ColumnContainerV5Styled>
									</TabChildV5>
								)}
							</TabViewV5>
						</div>
						<div
							className="col"
							data-cy="col-right"
						>
							<div className="rightHeader">{rightHeader}</div>
							<ColumnContainerV5Styled
								className="columnContainerV5"
								height={columnContainerHeight}
							>
								<Droppable
									droppableId="visible-columns"
									type="COLUMN"
								>
									{(provided) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
										>
											<ToggleContainerV5
												title="Available Fields"
												open={true}
												className={ReactUtils.cls("group", {prohibited: prohibitedDropDisplayFieldsArea})}
												hideTitle={!linkedXyiconFieldsOn}
											>
												{visibleColumns
													.filter((col) => col.field.refId !== REPORT_COUNT_FIELD || !linkedXyiconFieldsOn)
													.map((col, i) => (
														<Draggable
															key={`visible-${col.field.refId}`}
															draggableId={col.field.refId}
															index={i}
															isDragDisabled={allVisibleColumnsAreSelected || onlyOneVisibleColumns}
														>
															{(provided, snapshot) => this.renderField(col, "availableFields", provided, snapshot, false)}
														</Draggable>
													))}
												{provided.placeholder}
											</ToggleContainerV5>
										</div>
									)}
								</Droppable>
								{linkedXyiconFieldsOn && (
									<Droppable
										droppableId="visible-linkedFields-columns"
										type="COLUMN"
									>
										{(provided) => (
											<div
												{...provided.droppableProps}
												ref={provided.innerRef}
											>
												<ToggleContainerV5
													title="Linked Xyicon Fields"
													open={true}
													className={ReactUtils.cls("group", {prohibited: prohibitedDropDisplayLinkedFieldsArea})}
												>
													{visibleLinkedFieldsColumns.map((col, i) => (
														<Draggable
															key={`visible-linkedFields-${col.field.refId}`}
															draggableId={`linkedFields-${col.field.refId}`}
															index={i}
															isDragDisabled={allVisibleColumnsAreSelected || onlyOneVisibleColumns}
														>
															{(provided, snapshot) => this.renderField(col, "linkedFields", provided, snapshot, false)}
														</Draggable>
													))}
													{provided.placeholder}
												</ToggleContainerV5>
											</div>
										)}
									</Droppable>
								)}
							</ColumnContainerV5Styled>
						</div>
					</div>
				</DragDropContext>
			</ColumnEditorStyled>
		);
	}
}
